/**
 * The one frame every workflow is read inside (DOP-478).
 *
 * A workflow is a configured template plus its output plus its run history,
 * and that is true whichever template it came from — so the header, the tabs
 * and the optional docked rail are the same for all of them and only the body
 * changes. KPI, monitoring and loan tape each fill this in a wave-2 ticket.
 *
 * The active tab is read STRAIGHT from `?tab=` rather than mirrored into state
 * (decision D6, the monitoring page's model): the URL is then the only copy,
 * so Back moves between tabs and a link opens on the tab it names. A page that
 * has no use for a tab omits it from `tabs`; the shell does not invent one.
 */

import React from "react";
import { useSearchParams } from "react-router-dom";
import PageTabs, { type PageTab } from "./PageTabs";
import BackButton from "../BackButton";

/** The query key the shell reads its tab from. */
export const SHELL_TAB_PARAM = "tab";

export const DASHBOARD_TABS = [
	{ id: "data", label: "Data" },
	{ id: "review", label: "Needs review" },
	{ id: "history", label: "History" },
	{ id: "settings", label: "Settings" },
] as const;

export type DashboardTabId = (typeof DASHBOARD_TABS)[number]["id"];

export const DEFAULT_DASHBOARD_TAB: DashboardTabId = "data";

export interface DashboardShellProps {
	title: string;
	/** One line under the title: type, cadence, scope — whatever identifies it. */
	meta?: React.ReactNode;
	/** Primary and secondary buttons, right-aligned in the header. */
	actions?: React.ReactNode;
	/**
	 * The tabs this workflow actually has, in order. A tab may carry a count
	 * (Needs review is the one that does). Defaults to all four.
	 */
	tabs?: readonly PageTab[];
	/** Rendered to the right of the tabs — the Analyst toggle lives here. */
	tabActions?: React.ReactNode;
	/** Given the tab the URL names, so the caller renders one body. */
	children: (tab: string) => React.ReactNode;
	/** Docked to the right of the body at its own width when present. */
	rightRail?: React.ReactNode;
}

const DashboardShell: React.FC<DashboardShellProps> = ({
	title,
	meta,
	actions,
	tabs: tabsProp = DASHBOARD_TABS,
	tabActions,
	children,
	rightRail,
}) => {
	// Widened: the default's literal type has no `disabled`.
	const tabs: readonly PageTab[] = tabsProp;
	const [searchParams, setSearchParams] = useSearchParams();
	const requested = searchParams.get(SHELL_TAB_PARAM);
	// A `?tab=` naming a tab this workflow does not have reads as the default
	// rather than as an empty page: the link is stale, the workflow is not.
	// A disabled tab reads the same way: it has nothing to show yet.
	const activeTab =
		requested && tabs.some((tab) => tab.id === requested && !tab.disabled)
			? requested
			: (tabs.find((tab) => !tab.disabled)?.id ?? DEFAULT_DASHBOARD_TAB);

	const selectTab = (id: string) => {
		const next = new URLSearchParams(searchParams);
		next.set(SHELL_TAB_PARAM, id);
		// Pushed, so Back returns to the tab the user came from.
		setSearchParams(next);
	};

	return (
		<div className="flex h-full min-h-0 min-w-0 flex-1 overflow-hidden bg-gray-50">
			<div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
				<header className="flex items-start justify-between gap-4 px-6 pb-3 pt-6">
					<div className="flex min-w-0 items-start gap-3">
						{/* Up, not Back (DOP-485). The sidebar stopped listing the
						    workflows, so a page opened from a chat link or a toast had
						    no way to the list it belongs to except the nav entry —
						    which is a different gesture from "the thing I am inside of
						    is one of these". It lives in the shell, so every workflow
						    page has it and none of them has its own version. */}
						<BackButton to="/workflows" label="Back to Workflows" />
						<div className="min-w-0">
							<h1 className="truncate text-xl font-semibold text-text-base">
								{title}
							</h1>
							{meta && <p className="mt-1 text-sm text-text-muted">{meta}</p>}
						</div>
					</div>
					{actions && (
						<div className="flex flex-shrink-0 items-center gap-2">{actions}</div>
					)}
				</header>

				<PageTabs
					tabs={tabs}
					activeId={activeTab}
					onSelect={selectTab}
					ariaLabel="Workflow sections"
					actions={tabActions}
				/>

				<div className="min-h-0 min-w-0 flex-1 overflow-y-auto px-6 py-5">
					{children(activeTab)}
				</div>
			</div>
			{rightRail}
		</div>
	);
};

export default DashboardShell;
